import { useServicesContext } from "@modules/core/services/services.context"
import { type ExploreProgramBundleItemDto } from "@modules/explore/gateway/exploreDto"
import { type ProgramBundleDto, ProgramBundleType } from "@modules/programBundle/api/programBundleDto"
import {
  ProgramBundleDraftEntity,
  ProgramBundleEntity,
  ProgramBundleItemEntity,
} from "@modules/programBundle/entities/programBundleEntity"
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query"

export const useProgramBundle = (programBundleId: string) => {
  const { programBundleApi } = useServicesContext()

  const { data, isLoading } = useSuspenseQuery({
    queryKey: ["programBundle", programBundleId],
    queryFn: async () => {
      const result = await programBundleApi.getProgramBundle(programBundleId)
      return result
    },
  })

  return {
    data: ProgramBundleEntity.fromDto(data),
    isLoading,
  }
}

export const useProgramBundles = (filter: ProgramBundleType | null, onlyWithPrice?: boolean) => {
  const { programBundleApi } = useServicesContext()
  const onlyWithPriceDefault = onlyWithPrice ?? false

  const { data, isLoading } = useQuery({
    queryKey: ["programBundles", filter, onlyWithPriceDefault],
    queryFn: async () => {
      return await programBundleApi.getProgramBundles(filter, onlyWithPriceDefault)
    },
  })

  return {
    data: data?.map((programBundle: ProgramBundleDto) => ProgramBundleEntity.fromDto(programBundle)) || [],
    isLoading,
  }
}

export const useProgramBundlesSubscribed = () => {
  const { programBundleApi } = useServicesContext()

  const { data, isLoading } = useQuery({
    queryKey: ["programBundlesSubscribed"],
    queryFn: async () => {
      return await programBundleApi.getProgramBundlesSubscribed()
    },
  })

  return {
    data:
      data?.map((programBundle: ExploreProgramBundleItemDto) => ProgramBundleItemEntity.fromDto(programBundle)) || [],
    isLoading,
  }
}

export const useProgramBundlesCreation = (): {
  createProgramBundle: (programBundle: ProgramBundleDraftEntity) => Promise<ProgramBundleEntity>
  isPending: boolean
} => {
  const { programBundleApi } = useServicesContext()
  const queryClient = useQueryClient()

  const { mutateAsync: createProgramBundle, isPending: isCreateProgramBundle } = useMutation({
    mutationFn: async (programBundle: ProgramBundleDraftEntity) => {
      const result = await programBundleApi.createProgramBundle(ProgramBundleDraftEntity.toDto(programBundle))
      return ProgramBundleEntity.fromDto(result)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["programBundles"],
      })
    },
  })

  const isPending = isCreateProgramBundle

  return {
    createProgramBundle,
    isPending,
  }
}

export const useProgramBundleEdition = (
  programBundleId?: string,
): {
  updateProgramBundle: (programBundle: ProgramBundleDraftEntity) => Promise<void>
  isPending: boolean
} => {
  const { programBundleApi } = useServicesContext()
  const queryClient = useQueryClient()

  const { mutateAsync: updateProgramBundle, isPending: isUpdateProgramBundle } = useMutation({
    mutationFn: async (programBundle: ProgramBundleDraftEntity) => {
      if (programBundleId) {
        await programBundleApi.updateProgramBundle(programBundleId, ProgramBundleDraftEntity.toDto(programBundle))
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["programBundle", programBundleId],
      })
      queryClient.invalidateQueries({
        queryKey: ["programBundles"],
      })
    },
  })

  const isPending = isUpdateProgramBundle

  return {
    updateProgramBundle,
    isPending,
  }
}

export const useProgramBundleDelete = () => {
  const { programBundleApi } = useServicesContext()
  const queryClient = useQueryClient()

  const { mutateAsync: deleteProgramBundle, isPending } = useMutation({
    mutationFn: async (programBundleId: string) => {
      await programBundleApi.deleteProgramBundle(programBundleId)
      return programBundleId
    },
    onSuccess: (programBundleId: string) => {
      queryClient.invalidateQueries({
        queryKey: ["programBundle", programBundleId],
      })
      queryClient.invalidateQueries({
        queryKey: ["programBundles"],
      })
    },
  })

  return {
    deleteProgramBundle,
    isPending,
  }
}
